import React, { useState } from "react";
import {
  Modal,
  Typography,
  Input,
  Button,
  Row,
  Col,
  Form,
  notification,
  Grid,
  Select,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { InstantCallRequest } from "../../types/InstantCall";
import { instantCallService } from "../../lib/services/InstantCallFirebaseService";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

const InstantCallModal = ({ visible, onClose, propertyId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const screens = useBreakpoint();

  // Country codes for popular countries
  const countryCodes = [
    { code: "+971", country: "UAE" },
    { code: "+966", country: "Saudi Arabia" },
    { code: "+20", country: "Egypt" },
    { code: "+90", country: "Turkey" },
    { code: "+91", country: "India" },
    { code: "+92", country: "Pakistan" },
    { code: "+1", country: "USA/Canada" },
    { code: "+44", country: "UK" },
    { code: "+33", country: "France" },
    { code: "+49", country: "Germany" },
    { code: "+7", country: "Russia" },
    { code: "+86", country: "China" },
    { code: "+81", country: "Japan" },
    { code: "+82", country: "South Korea" },
    { code: "+65", country: "Singapore" },
    { code: "+60", country: "Malaysia" },
  ];

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const newRequest = new InstantCallRequest({
        fullName: values.fullName,
        phoneNumber: values.phoneNumber,
        countryCode: values.countryCode || "+971",
        propertyId: propertyId,
      });

      await instantCallService.addInstantCallRequest(newRequest);

      notification.success({
        message: "Request Sent",
        description: "Thank you! We will contact you back within two hours.",
      });
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Submission failed:", error);
      notification.error({
        message: "Submission Failed",
        description:
          "There was an issue sending your request. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fixed filter function
  const filterOption = (input, option) => {
    if (!option || !option.children) return false;
    const text = String(option.children).toLowerCase();
    const search = input.toLowerCase();
    return text.includes(search);
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={screens.xs ? "100vw" : 500}
      closeIcon={
        <CloseOutlined style={{ color: "#011F3A", fontSize: "16px" }} />
      }
      style={{
        maxWidth: "100vw",
        padding: "0px",
      }}
      bodyStyle={{
        padding: screens.xs ? "20px 16px" : "12px 10px",
      }}
    >
      <Title
        level={4}
        style={{
          color: "#011F3A",
          fontWeight: 600,
          marginTop: 0,
          marginBottom: screens.xs ? 20 : 24,
          fontSize: screens.xs ? "18px" : "20px",
        }}
      >
        INSTANT CALL
      </Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ countryCode: "+971" }}
      >
        {/* Full Name Input */}
        <Form.Item
          label={
            <Text
              style={{
                fontWeight: 500,
                color: "#444",
                fontSize: screens.xs ? "13px" : "14px",
              }}
            >
              FULL NAME
            </Text>
          }
          name="fullName"
          rules={[{ required: true, message: "Please enter your full name!" }]}
          style={{ marginBottom: screens.xs ? 16 : 20 }}
        >
          <Input
            placeholder="Enter your Full Name"
            style={{
              height: screens.xs ? 38 : 40,
              borderColor: "#ccc",
              fontSize: screens.xs ? "14px" : "15px",
            }}
          />
        </Form.Item>

        {/* Phone Number Input */}
        <Form.Item
          label={
            <Text
              style={{
                fontWeight: 500,
                color: "#444",
                fontSize: screens.xs ? "13px" : "14px",
              }}
            >
              Phone Number
            </Text>
          }
          style={{ marginBottom: screens.xs ? 30 : 36 }}
        >
          <Row gutter={screens.xs ? 6 : 8}>
            {/* Country Code Select */}
            <Col flex={screens.xs ? "100px" : "110px"}>
              <Form.Item name="countryCode" noStyle>
                <Select
                  style={{
                    height: screens.xs ? 38 : 40,
                    width: "100%",
                  }}
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOption} // Use the fixed function
                >
                  {countryCodes.map((country) => (
                    <Option key={country.code} value={country.code}>
                      {country.code} ({country.country})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {/* Phone Number Field */}
            <Col flex="auto">
              <Form.Item
                name="phoneNumber"
                rules={[
                  {
                    required: true,
                    message: "Please enter your phone number!",
                  },
                  {
                    pattern: /^[0-9]+$/,
                    message: "Please enter a valid number.",
                  },
                  {
                    min: 5,
                    message: "Phone number must be at least 5 digits.",
                  },
                  {
                    max: 15,
                    message: "Phone number must be less than 15 digits.",
                  },
                ]}
                noStyle
              >
                <Input
                  placeholder="Enter your phone number"
                  style={{
                    height: screens.xs ? 38 : 40,
                    borderColor: "#ccc",
                    fontSize: screens.xs ? "14px" : "15px",
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>

        {/* Privacy Policy Text */}
        <div style={{ marginTop: screens.xs ? 8 : 12 }}>
          <Text
            style={{
              display: "block",
              marginBottom: screens.xs ? 16 : 20,
              fontSize: screens.xs ? "11px" : "12px",
              color: "#555",
              lineHeight: 1.4,
            }}
          >
            Visit the{" "}
            <a href="/privacy" style={{ color: "#011F3A", fontWeight: "bold" }}>
              Privacy Policy
            </a>{" "}
            to understand how we handle your personal data
          </Text>
        </div>

        {/* Submit Button */}
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{
              backgroundColor: "#011F3A",
              borderColor: "#011F3A",
              color: "#fff",
              height: screens.xs ? 42 : 45,
              fontSize: screens.xs ? "15px" : "16px",
              fontWeight: 600,
              borderRadius: 4,
              width: "100%",
            }}
          >
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InstantCallModal;
